import React from 'react';
import { motion } from 'framer-motion';
import { Star, Quote, MapPin, Calendar, Heart, Calendar as CalendarIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSiteConfig } from '../contexts/SiteConfigContext';

const Reviews: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { config } = useSiteConfig();

  // Mapped from site configuration context
  const reviews = config.reviews.map((r, index) => ({
    id: parseInt(r.reviewId) || (index + 1),
    name: r.displayName,
    location: "Seminyak, Bali",
    rating: r.starRating,
    date: r.createTime,
    occasion: index % 2 === 0 ? "Birthday" : "Wedding",
    review: r.comment,
    images: []
  }));

  const stats = [
    { label: i18n.language === 'id' ? "Total Ulasan" : "Total Reviews", value: `${config.googleReviewCount}+`, icon: Star },
    { label: i18n.language === 'id' ? "Rata-rata Rating" : "Average Rating", value: config.googleRating.toFixed(1), icon: Heart },
    { label: i18n.language === 'id' ? "Pelanggan Puas" : "Happy customers", value: "4,000+", icon: Quote },
    { label: i18n.language === 'id' ? "Tahun Pengalaman" : "Experiences years", value: "10+", icon: Calendar }
  ];

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === 'id' ? 'id-ID' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        className={`w-4 h-4 ${index < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
          }`}
      />
    ));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-pink-50">
      {/* Hero Section */}
      <section className="relative pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-red-400 to-pink-500 bg-clip-text text-transparent">
              Customer Reviews
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {i18n.language === 'id'
                ? 'Lihat apa yang dikatakan pelanggan kami tentang kue-kue istimewa yang kami buat untuk momen berharga mereka.'
                : 'See what our customers say about the special cakes we create for their precious moments.'
              }
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-red-400 to-pink-500 rounded-full mb-4">
                  <stat.icon className="w-8 h-8 text-white" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</div>
                <div className="text-gray-600 text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Google Reviews Integration */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
              {i18n.language === 'id' ? 'Ulasan Google' : 'Google Reviews'}
            </h2>
            <div className="flex items-center justify-center gap-4 mb-8">
              <div className="flex items-center gap-2">
                <div className="flex">
                  {renderStars(Math.round(config.googleRating))}
                </div>
                <span className="text-2xl font-bold text-gray-900">{config.googleRating.toFixed(1)}</span>
              </div>
              <div className="w-px h-8 bg-gray-300"></div>
              <div className="text-gray-600">
                <span className="font-semibold">{config.googleReviewCount}</span> {i18n.language === 'id' ? 'ulasan' : 'reviews'}
              </div>
            </div>
            <a
              href="https://www.google.com/maps/place/TN+Cakes+Bali/@-8.6837973,115.1709839,17z/data=!4m8!3m7!1s0x2dd24724ada5a7f7:0x4aca823c3722187c!8m2!3d-8.6837973!4d115.1709839!9m1!1b1!16s%2Fg%2F11gbxbmhls?entry=ttu&g_ep=EgoyMDI1MDczMC4wIKXMDSoASAFQAw%3D%3D"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-white px-6 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 border border-gray-200"
            >
              {/* Google Logo */}
              <div className="flex-shrink-0">
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                </svg>
              </div>
              <span className="font-medium text-gray-900">
                {i18n.language === 'id' ? 'Lihat Semua Ulasan Google' : 'View All Google Reviews'}
              </span>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Customer Reviews */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
              {i18n.language === 'id' ? 'Testimoni Pelanggan' : 'Customer Testimonials'}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {reviews.map((review, index) => (
              <motion.div
                key={review.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-gray-900 text-lg">{review.name}</h3>
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                      <MapPin className="w-4 h-4" />
                      {review.location}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Calendar className="w-4 h-4" />
                      {formatDate(review.date)}
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-red-400 to-pink-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                    {review.occasion}
                  </div>
                </div>

                {/* Rating */}
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex">{renderStars(review.rating)}</div>
                  <span className="text-sm text-gray-600">({review.rating}.0)</span>
                </div>

                {/* Review Text */}
                <div className="mb-4">
                  <Quote className="w-6 h-6 text-red-400 mb-2" />
                  <p className="text-gray-700 leading-relaxed">{review.review}</p>
                </div>

                {/* Images */}
                {review.images.length > 0 && (
                  <div className="flex gap-2">
                    {review.images.map((image, imgIndex) => (
                      <div key={imgIndex} className="relative group">
                        <img
                          src={image}
                          alt={`${review.name}'s cake`}
                          className="w-16 h-16 rounded-lg object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-black/20 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                          <Camera className="w-4 h-4 text-white" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-red-400 to-pink-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {t('globalCTA.title')}
            </h2>
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              {t('globalCTA.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/inquiry"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-red-500 font-bold rounded-full hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                {t('globalCTA.orderNow')}
              </a>
              <a
                href="/portfolio"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-bold rounded-full hover:bg-white hover:text-red-500 transition-all duration-300 transform hover:scale-105"
              >
                {t('globalCTA.viewGallery')}
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Reviews;
